import React from "react";
import { HexColorPicker } from "react-colorful";
import { Eyedropper, X } from "@phosphor-icons/react";
import styled from "styled-components";

import { useEyedropper } from "../../contexts/EyedropperContext";

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

function normalizeHex(input) {
  if (!input) return null;
  const s = input.trim();
  if (!HEX_RE.test(s)) return null;
  return (s.startsWith("#") ? s : `#${s}`).toLowerCase();
}

// `onChange(hex)` fires for every commit (picker drag, hex input commit,
// eyedropper click). The eyedropper session is owned internally — when
// active, the editor stays open, hover repaints the preview swatch, and
// click commits via onChange. Toggling the picker off, pressing Escape,
// or unmounting all clean up the App-level eyedropper state.
function ColorEditor({ value, onChange, onClose }) {
  const { available, beginEyedropper, endEyedropper } = useEyedropper();
  const [draft, setDraft] = React.useState(value);
  const [picking, setPicking] = React.useState(false);
  const [preview, setPreview] = React.useState(null);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  // Cleanup: if the editor unmounts (popover closes) while an eyedropper
  // session is live, end it so a stray click on Marc doesn't fire into a
  // ghost handler.
  React.useEffect(() => {
    return () => {
      if (picking) endEyedropper();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitHex = (raw) => {
    const next = normalizeHex(raw);
    if (next) onChange(next);
  };

  const startPicking = () => {
    setPicking(true);
    setPreview(null);
    beginEyedropper({
      onPreview: (hex) => setPreview(hex),
      onPick: (hex) => onChange(hex),
      onCancel: () => {
        setPicking(false);
        setPreview(null);
      },
    });
  };

  const stopPicking = () => {
    endEyedropper();
    setPicking(false);
    setPreview(null);
  };

  return (
    <Wrap>
      {onClose && (
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <X size={14} weight="bold" />
        </CloseButton>
      )}
      <HexColorPicker color={value} onChange={onChange} />
      <HexInput
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commitHex(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitHex(e.currentTarget.value);
          }
        }}
        spellCheck={false}
        aria-label="Hex color"
      />
      {available &&
        (picking ? (
          <PickRow>
            <PickButton type="button" onClick={stopPicking} $active>
              <X size={14} weight="bold" />
              <span>Stop picking</span>
            </PickButton>
            <PreviewSwatch
              $hex={preview ?? value}
              $dim={!preview}
              title={preview ? `Hovering ${preview}` : "Hover Marc to preview"}
            />
          </PickRow>
        ) : (
          <PickButton type="button" onClick={startPicking}>
            <Eyedropper size={14} weight="fill" />
            <span>Pick from Marc</span>
          </PickButton>
        ))}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 200px;

  .react-colorful {
    width: 100%;
    height: 160px;
  }
  .react-colorful__saturation {
    border-radius: 6px 6px 0 0;
  }
  .react-colorful__last-control {
    border-radius: 0 0 6px 6px;
  }

  /* Match both pointers visually — by default the hue thumb is smaller
     and blends into the rainbow strip, which reads as decoration rather
     than a draggable control. Same size + white ring for both makes the
     hue strip obviously interactive. */
  .react-colorful__pointer {
    width: 20px;
    height: 20px;
    border: 3px solid white;
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.3),
      0 2px 4px rgba(0, 0, 0, 0.25);
  }
`;

const HexInput = styled.input`
  width: 100%;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  padding: 6px 8px;
  border: 1px solid var(--color-orange-300);
  border-radius: 6px;
  background: var(--color-orange-50, #fff8f1);
  color: var(--color-text);
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--color-orange-700);
  }
`;

const PickRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: stretch;
`;

const PickButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-orange-500)" : "var(--color-orange-300)")};
  border-radius: 6px;
  background: ${(p) =>
    p.$active ? "var(--color-orange-300)" : "var(--color-orange-100)"};
  color: var(--color-text);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  cursor: pointer;

  &:hover {
    background: var(--color-orange-300);
    border-color: var(--color-orange-400);
  }
`;

const PreviewSwatch = styled.span`
  width: 30px;
  border-radius: 6px;
  border: 1px solid var(--color-orange-300);
  background: ${(p) => p.$hex};
  opacity: ${(p) => (p.$dim ? 0.3 : 1)};
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-orange-100);
  border: 1px solid var(--color-orange-300);
  border-radius: 50%;
  color: var(--color-text-muted);
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: var(--color-orange-300);
    color: var(--color-text);
  }
`;

export default ColorEditor;
